import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

/**
 * PAX-7034 — Alpine component voor de specialiteiten-tab op de rennerfiche.
 * Combineert slider-invoer met een live-updatende radargrafiek.
 *
 * @param {Object} init  { rennerId, csrfToken, canWrite, dimensies, scores }
 */
export function specialiteitEnkelTab(init) {
  return {
    rennerId:  init.rennerId,
    csrfToken: init.csrfToken,
    canWrite:  init.canWrite,
    dimensies: init.dimensies,
    scores:    Object.fromEntries(init.scores.map(s => [s.dimensie_id, s.score])),
    saving:    false,
    saveMsg:   '',
    saveOk:    true,
    _chart:    null,
    _observer: null,

    init() {
      this.$nextTick(() => this._renderRadar());
      this._observer = new MutationObserver(() => {
        if (this._chart) { this._chart.destroy(); this._chart = null; }
        this._renderRadar();
      });
      this._observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });
    },

    destroy() {
      this._observer?.disconnect();
      this._chart?.destroy();
    },

    _theme() {
      const dark = document.documentElement.classList.contains('dark');
      return {
        grid:    dark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)',
        ticks:   dark ? '#9ca3af' : '#6b7280',
        tooltip: dark ? '#1f2937' : '#ffffff',
        fill:    dark ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.10)',
        border:  dark ? 'rgba(59,130,246,0.6)'  : 'rgba(59,130,246,0.5)',
      };
    },

    _renderRadar() {
      const canvas = this.$refs?.radarCanvas;
      if (!canvas) return;

      const th     = this._theme();
      const labels = this.dimensies.map(d => d.naam);
      const data   = this.dimensies.map(d => this.scores[d.id] ?? 0);

      if (this._chart) {
        this._chart.data.datasets[0].data = data;
        this._chart.update('none');
        return;
      }

      this._chart = new Chart(canvas, {
        type: 'radar',
        data: {
          labels,
          datasets: [{
            data,
            fill:                 true,
            backgroundColor:      th.fill,
            borderColor:          th.border,
            borderWidth:          2,
            pointRadius:          3,
            pointBackgroundColor: th.border,
          }],
        },
        options: {
          animation:           { duration: 200 },
          responsive:          true,
          maintainAspectRatio: true,
          plugins: {
            legend:  { display: false },
            tooltip: {
              backgroundColor: th.tooltip,
              titleFont:  { size: 11 },
              bodyFont:   { size: 12, weight: 'bold' },
              callbacks:  { label: (ctx) => ' ' + ctx.raw },
            },
          },
          scales: {
            r: {
              min:      0,
              max:      100,
              ticks: {
                stepSize:      20,
                color:         th.ticks,
                font:          { size: 9 },
                backdropColor: 'transparent',
              },
              grid:        { color: th.grid },
              angleLines:  { color: th.grid },
              pointLabels: { color: th.ticks, font: { size: 11 } },
            },
          },
        },
      });
    },

    getScore(dimId) {
      const val = this.scores[dimId];
      return val !== undefined ? val : null;
    },

    setScore(dimId, rawValue) {
      if (rawValue === '' || rawValue === null) {
        this.scores[dimId] = null;
      } else {
        const v = parseInt(rawValue, 10);
        this.scores[dimId] = isNaN(v) ? null : Math.max(0, Math.min(100, v));
      }
      if (this._chart) {
        this._chart.data.datasets[0].data = this.dimensies.map(d => this.scores[d.id] ?? 0);
        this._chart.update('none');
      }
    },

    async opslaan() {
      if (!this.canWrite) return;
      this.saving  = true;
      this.saveMsg = '';

      const payload = this.dimensies.map(dim => ({
        dimensie_id: dim.id,
        score:       this.scores[dim.id] !== undefined ? this.scores[dim.id] : null,
      }));

      try {
        const res = await fetch(`/admin/renners/${this.rennerId}/specialiteiten`, {
          method:  'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.csrfToken,
          },
          body: JSON.stringify({ scores: payload }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.ok) {
          this.saveOk  = true;
          this.saveMsg = 'Scores opgeslagen.';
        } else {
          this.saveOk  = false;
          this.saveMsg = data.message || 'Er is een fout opgetreden.';
        }
      } catch {
        this.saveOk  = false;
        this.saveMsg = 'Netwerkfout — probeer opnieuw.';
      } finally {
        this.saving = false;
        setTimeout(() => { this.saveMsg = ''; }, 4000);
      }
    },
  };
}

/**
 * PAX-7027 — Alpine component voor specialiteiten radar-charts.
 *
 * @param {Object} kaartenData  { [rennerId]: { scores: number[], heeft_scores: bool, ... } }
 * @param {Object} dimensiesData { labels: string[], kleuren: string[] }
 */
export function specialiteitenRadar(kaartenData, dimensiesData) {
  return {
    _charts: {},
    _observer: null,

    init() {
      this.$nextTick(() => this._renderAll());
      this._observer = new MutationObserver(() => {
        Object.values(this._charts).forEach((c) => c?.destroy());
        this._charts = {};
        this._renderAll();
      });
      this._observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });
    },

    destroy() {
      this._observer?.disconnect();
      Object.values(this._charts).forEach((c) => c?.destroy());
    },

    _theme() {
      const dark = document.documentElement.classList.contains('dark');
      return {
        grid:    dark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)',
        ticks:   dark ? '#9ca3af' : '#6b7280',
        tooltip: dark ? '#1f2937' : '#111827',
        fill:    dark ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.10)',
        border:  dark ? 'rgba(59,130,246,0.6)'  : 'rgba(59,130,246,0.5)',
      };
    },

    _renderAll() {
      const th = this._theme();
      const labels = dimensiesData.labels ?? [];

      Object.keys(kaartenData).forEach((rennerId) => {
        const kaart = kaartenData[rennerId];
        if (!kaart.heeft_scores) {
          return;
        }

        const canvas = document.getElementById('radar-' + rennerId);
        if (!canvas) {
          return;
        }

        if (this._charts[rennerId]) {
          this._charts[rennerId].destroy();
        }

        const scores = kaart.scores ?? [];

        this._charts[rennerId] = new Chart(canvas, {
          type: 'radar',
          data: {
            labels,
            datasets: [
              {
                data:            scores,
                fill:            true,
                backgroundColor: th.fill,
                borderColor:     th.border,
                borderWidth:     2,
                pointRadius:     3,
                pointBackgroundColor: th.border,
              },
            ],
          },
          options: {
            animation:   { duration: 300 },
            responsive:  true,
            maintainAspectRatio: true,
            plugins: {
              legend:  { display: false },
              tooltip: {
                backgroundColor: th.tooltip,
                titleFont:  { size: 11 },
                bodyFont:   { size: 12, weight: 'bold' },
                callbacks: {
                  label: (ctx) => ' ' + ctx.raw,
                },
              },
            },
            scales: {
              r: {
                min:         0,
                max:         100,
                ticks: {
                  stepSize:   20,
                  color:      th.ticks,
                  font:       { size: 9 },
                  backdropColor: 'transparent',
                },
                grid:        { color: th.grid },
                angleLines:  { color: th.grid },
                pointLabels: {
                  color: th.ticks,
                  font:  { size: 10 },
                },
              },
            },
          },
        });
      });
    },
  };
}
